import type { tTikTokRichContent } from 'src/pages/TikTok/TikTok.types';
import type { tCloseResult } from 'src/components/LeadClose/LeadClose.types';
import React, { Dispatch, SetStateAction } from 'react';
import { eLeadbotState } from '../../pages/MessengerV2/Messenger.enum.ts';
import { tFunnelsWithStages } from '../../pages/Pipe/Pipe.types.ts';
import {
  eChannelType,
  eLeadStatus,
} from '../../providers/SocketsProvider/Sockets.enums.ts';
import {
  tConversationSummary,
  tSummaryEligibilityResponse,
  tSummaryError,
} from '../../types/summary.types.ts';
import { tMessageSource } from '../../types/messageSources.types.ts';
import type { tLeadAgentPaused } from './components/MessageBar/useLeadAgentPaused.hook';
import type { tUseLeadAgentChatBanner } from './components/MessageBar/useLeadAgentChatBanner.hook';
import {
  CalendarEventType,
  ChatMessageContainerEnum,
  ChatState,
  ChatTextAreaContainerEnum,
  eAction,
  eField,
  EventType,
  MediaType,
  MessageErrorType,
  MessageState,
  NoteType,
} from './Chat.enum.ts';
import { ChatInfoEnum } from './components/ChatInfo/ChatInfo.enum.ts';
import { tQuickActionState } from './components/MessageBar/MessageBar.types.ts';
import { tSentTemplateCounter } from '../../pages/WhatsappApi/components/TemplatesTable/SentTemplateCounter.types';
import { tUndeliveredMessage } from 'src/pages/WhatsappApi/components/TemplatesTable/UndeliveredMessage.types.ts';
import { tCard } from 'src/pages/WhatsappApi/WhatsappApi.types.ts';
import { tWhatsAppWebConnectionStatus } from './useWhatsAppWebConnection.hook.ts';
import { tComposerSealQuoteScope } from '../../pages/Wallet/Wallet.types.ts';

export type tChatContext = {
  selectedTab: ChatInfoEnum | undefined;
  setSelectedTab: (tab: ChatInfoEnum) => void;
  selectedMessageContainer: ChatMessageContainerEnum | undefined;
  setSelectedMessageContainer: (container: ChatMessageContainerEnum) => void;
  selectedTextAreaContainer: ChatTextAreaContainerEnum | undefined;
  setSelectedTextAreaContainer: (container: ChatTextAreaContainerEnum) => void;
  selectedChat: tCurrentChat | null;
  setSelectedChat: Dispatch<SetStateAction<tCurrentChat | null>>;
  selectedChatLeadId: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  blockTextArea: boolean;
  setBlockTextArea: (block: boolean) => void;
  /**
   * RET-2232 D-16: el veredicto del wallet para el compositor de texto
   * libre, con un ÚNICO escritor (el efecto de `TextAreaContainer`).
   * `blockTextArea` es una bandera de "ocupado" compartida con seis
   * escritores más (`DriveFilesAction`, `GoogleMapsAction`,
   * `TemplateActions`, el propio `SendAction`, ...) que la limpian sin saber
   * nada del wallet — cualquiera de ellos puede escribir `false` mientras el
   * saldo sigue sin cubrir el envío. `SendAction` consulta ESTE campo
   * directamente para decidir si el clic manda algo, en vez de fiarse de la
   * bandera compartida. Opcional: la mayoría de consumidores de este
   * contexto no tocan el compositor con saldo y no necesitan proveerlo.
   */
  isComposerWalletBlocked?: boolean;
  setComposerWalletBlocked?: (blocked: boolean) => void;
  /**
   * RET-2232 D-28: la última cotización CONOCIDA del sello del compositor,
   * por (workspace, conversación) — un `useRef`, no `useState`: escribirlo
   * no debe causar un re-render del árbol del chat, y su única lectora/
   * escritora es `useComposerServiceSeal`. Vive aquí (no en
   * `TextAreaContainer`) por la misma razón que `isComposerWalletBlocked`:
   * `ChatEnum.WABTEMPLATES_VARIABLES` desmonta y vuelve a montar ese
   * componente sin cambiar de conversación, y este contexto es lo único de
   * por medio que NO se desmonta con él. Opcional: la mayoría de
   * consumidores de este contexto no tocan el compositor con saldo.
   */
  composerSealQuoteRef?: React.MutableRefObject<tComposerSealQuoteScope>;
  cursor: string;
  setCursor: (cursor: string) => void;
  rows: number;
  setRows: (rows: number) => void;
  messages: tChatEvents | undefined;
  setMessages: Dispatch<SetStateAction<tChatEvents | undefined>>;
  replyMessage: tChatEvent | null;
  setReplyMessage: Dispatch<SetStateAction<tChatEvent | null>>;
  resendIds: string[];
  setResendIds: (resendIds: string[]) => void;
  selectedChatsIds: string[];
  setSelectedChatsIds: (leadsIds: string[]) => void;
  selectMessageToReply: (selected: boolean, messageId: string) => void;
  funnelsWithStages: tFunnelsWithStages[] | null;
  getFunnelsWithStages: () => void;
  chatInputContainerRef: React.RefObject<HTMLDivElement>;
  updateChatEventsData: number;
  setUpdateChatEventsData: Dispatch<SetStateAction<number>>;
  textAreaContainerRef: React.RefObject<HTMLDivElement>;
  showAllowTypesBanner: boolean;
  setShowAllowTypesBanner: Dispatch<SetStateAction<boolean>>;
  followUps: tChatEvent[];
  setFollowUps: Dispatch<SetStateAction<tChatEvent[]>>;
  notes: tChatEvent[];
  setNotes: Dispatch<SetStateAction<tChatEvent[]>>;
  leadsalesAI: tChatEvent[];
  setLeadsalesAI: Dispatch<SetStateAction<tChatEvent[]>>;
  favoriteMessages: tChatEvent[];
  setFavoriteMessages: Dispatch<SetStateAction<tChatEvent[]>>;
  quickReplies: tQuickReply[];
  setQuickReplies: Dispatch<SetStateAction<tQuickReply[]>>;
  fetchQuickReplies: () => void;
  selectedNote: tSelectedNote | null;
  setSelectedNote: Dispatch<SetStateAction<tSelectedNote | null>>;
  selectedFollowUp: tSelectedFollowUp | null;
  setSelectedFollowUp: Dispatch<SetStateAction<tSelectedFollowUp | null>>;
  quickActionState: tQuickActionState;
  setQuickActionState: Dispatch<SetStateAction<tQuickActionState>>;
  isResending: boolean;
  setIsResending: Dispatch<SetStateAction<boolean>>;
  droppedAttachments: tChatAttachment[];
  setDroppedAttachments: Dispatch<SetStateAction<tChatAttachment[]>>;
  chatEvents: tChatEvent[];
  setChatEvents: React.Dispatch<React.SetStateAction<tChatEvent[]>>;
  /**
   * SRE-3328: the newest page of the open chat, fetched on request by
   * `returnToLatest` so an in-chat search can put the thread back where it
   * was before it jumped. MessageContainer applies it: it owns the anchored
   * window it replaces (and that window's forward cursor), so the fetch lives
   * here, beside the one other newest-page load, and the swap lives there.
   * `epoch` changes on every fetch, so returning twice to an unchanged page
   * still re-applies it.
   */
  latestPage: tLatestChatPage | null;
  /** Fetches the newest page of the open chat into `latestPage`. */
  returnToLatest: () => Promise<void>;
  /**
   * INT-1504: a request, from outside the composer, to open its template
   * picker. The Meta error notice inside a bubble has no access to the
   * composer's own state, so it leaves the request here; MessageBar opens the
   * picker and the picker consumes the request.
   */
  templatePickerRequest: tTemplatePickerRequest | null;
  setTemplatePickerRequest: Dispatch<
    SetStateAction<tTemplatePickerRequest | null>
  >;
  /**
   * AGI-500: a request, from Lead Agent's indicator in the thread, to take the
   * conversation. The take-over (switch Lead Agent off, then its survey) lives
   * with the composer, so the indicator leaves the request here and MessageBar
   * runs it and clears it.
   */
  takeConversationRequest: tTakeConversationRequest | null;
  setTakeConversationRequest: Dispatch<
    SetStateAction<tTakeConversationRequest | null>
  >;
  setNewFunnelID: React.Dispatch<React.SetStateAction<string>>;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  // Summary functionality
  conversationSummary: tConversationSummary | null;
  isSummaryLoading: boolean;
  summaryError: tSummaryError | null;
  summaryEligibility: tSummaryEligibilityResponse | null;
  generateSummary: (conversationId: string) => Promise<void>;
  checkSummaryEligibility: (conversationId: string) => Promise<void>;
  clearSummary: () => void;
  clearSummaryError: () => void;
  tagInputRef: React.RefObject<HTMLInputElement>;
  groupParticipants: tParticipant[] | undefined;
  setGroupParticipants: React.Dispatch<
    React.SetStateAction<tParticipant[] | undefined>
  >;
  autoRespondEnabled: boolean;
  setAutoRespondEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  // Live, non-cacheable WhatsApp Web connection status for the open chat
  // (INT-941 / U1). `phase === 'absent'` is the read-only trigger consumed
  // by the composer (U2) and the pre-send guard (U3). Inert (`phase: 'idle'`)
  // for non-WhatsApp-Web views.
  whatsAppWebConnection: tWhatsAppWebConnectionStatus;
  // Whether billing paused the workspace's Lead Agent, and why. Per workspace,
  // so it holds across chats.
  leadAgentPaused: tLeadAgentPaused;
  refetchLeadAgentPaused: () => void;
  // Whether the Owner closed the commercial pause notice for the whole workspace
  // (AGI-766, MLA-100), and `markDismissed` for the close made here.
  leadAgentChatBanner: tUseLeadAgentChatBanner;
};

/** INT-1504: messaging's answer to an accepted retry (via bff-web). */
export type tRetryWapiMessageResponse = {
  retryAttempt: number;
};

/** INT-1504: which filter the template picker opens with. */
export type tTemplatePickerRequest = {
  templateType: 'ALL' | 'UTILITY';
};

/** AGI-500: the chat whose conversation the person takes from Lead Agent. */
export type tTakeConversationRequest = {
  chatId: string;
};

export type tChatEventResponse<T> = {
  success: boolean;
  code: number;
  message: string;
  data: T;
};

export type tChatProps = {
  isChatModal?: boolean;
  onCloseModal?: () => void;
};

export type tLeadInfoProps = tChatProps & {};

export type tNoteAttachment = {
  media_type: string;
  url: string;
  mimetype: string;
};

export type tNote = {
  title: string;
  type: number;
};

export type tCreateNoteData = {
  id: string;
  content: {
    text: string;
    attachments: tNoteAttachment[];
  };
  created_by: string;
  title: string;
  note: tNote;
};

export type tCreateNoteRequest = {
  chatId: string;
  data: tCreateNoteData;
};

export type tCreateNoteResponse = {
  statusCode: number;
  message: string;
};

export type tLeadsalesAI = {
  title: string;
  type: number;
};

export type tCreateLeadsalesAIData = {
  id: string;
  content: {
    text: string;
    attachments: tNoteAttachment[];
  };
  created_by: string;
  title: string;
  leadsales_ai: tLeadsalesAI;
};

export type tCreateLeadsalesAIRequest = {
  chatId: string;
  data: tCreateLeadsalesAIData;
};

export type tCreateLeadsalesAIResponse = {
  statusCode: number;
  message: string;
};

export type tUpdateTagRequest = {
  workspaceId: string;
  chatId: string;
  attach_tags: string[];
};

export type tTagRequest = {
  attach_tags: string[];
};

export type tUpdateTagResponse = {
  statusCode: number;
  message: string;
};

export type tDeleteTagRequest = {
  workspaceId: string;
  chatId: string;
  detach_tags: string[];
};

export type tTagDeleteRequest = {
  detach_tags: string[];
};

export type tDeleteEventRequest = {
  eventId: string;
  workspaceId: string;
};

export type tDeleteEventResponse = {
  statusCode: number;
  message: string;
};

export type tUpdateEventAttachment = {
  media_type: MediaType;
  url: string;
  filename?: string;
  mimetype: string;
  latitude?: number;
  longitude?: number;
  ptt?: boolean;
  decryption?: object;
  vcard?: string;
  duration?: number;
};

export type tUpdateEventLink = {
  url: string;
  title?: string;
  description?: string;
  jpegThumbnail?: string;
};

export type tUpdateEventContent = {
  text?: string;
  attachments?: tUpdateEventAttachment[];
  quoted?: string;
  link_preview?: tUpdateEventLink;
};

export interface FollowUpContent {
  trigger_date: Date;
  consumed: boolean;
}

export interface NoteContent {
  title?: string;
  type: NoteType;
  sale_in_cents?: number;
}

export type UpdateNotePayload = Partial<NoteContent>;

export type tUpdateEventData = {
  id?: string;
  type?: EventType;
  is_favorite?: boolean;
  content?: tUpdateEventContent;
  follow_up?: Omit<FollowUpContent, 'consumed'>;
  note?: UpdateNotePayload;
  title?: string;
  auth_id?: string;
};

export type tUpdateEventRequest = {
  eventId: string;
  workspaceId: string;
  data: tUpdateEventData;
};

export type tSelectedNote = {
  id: string;
  title: string;
  content: string;
  attachments?: tAttachment[];
};

export type tSelectedFollowUp = {
  id: string;
  trigger_date: Date;
  consumed: boolean;
  /**
   * BUG-741: whether this card is a WAPI scheduled template rather than a plain
   * follow-up. Without it the generic edit flow could not tell the two apart and
   * would rewrite only the display card, leaving the real WBS campaign untouched.
   */
  isWapiTemplate?: boolean;
  /**
   * BUG-741: the WBS campaign that actually sends the template. Carried here so a
   * future template-specific edit flow can act on the real send instead of the
   * generic `updateEvent`. Null for regular follow-ups.
   */
  campaignId?: number | null;
  content: {
    text: string;
    attachments?: tAttachment[];
  };
};

export type tUpdateEventResponse = {
  statusCode: number;
  message: string;
};

export type tCreateFollowUpData = {
  id: string;
  channel_type: string;
  content: {
    text: string;
    attachments?: tNoteAttachment[];
  };
  trigger_date: string;
  created_by: string;
  is_wapi_template?: boolean;
  // WBS campaign that sends this scheduled WAPI template (BUG-687). Persisted
  // on the follow-up so deleting the card can cancel the campaign.
  campaign_id?: number;
};

export type tCreateFollowUpRequest = {
  chatId: string;
  data: tCreateFollowUpData;
};

export type tCreateFollowUpResponse = {
  statusCode: number;
  id: string;
  message: string;
};

export type DeleteChatEventPayload = {
  channel_type?: string;
  auth_id?: string;
  workspace_id: string;
};

export type tForwardRequest = {
  workspaceId: string;
  channelType: string;
  toHandles: string[];
  createdBy: string;
};

/**
 * One destination of POST /chats/chats/:chatId/forward-v2/:eventId. `error_code`
 * (chat codes 1016-1018) is set only when the outbound send gate refused it.
 */
export type tForwardEventResult = {
  chat_id: string;
  message_id?: string;
  state?: number;
  error?: string;
  error_code?: number;
};

/** forward-v2 answers 200 whatever happens to each destination. */
export type tForwardEventResponse = {
  total?: number;
  success?: number;
  errors?: number;
  results?: tForwardEventResult[];
};

export type tChat = {
  id: string;
  leadId: string;
};

export type tChatData = {
  chats: tChat[];
};

export type tChatResponse = {
  data: tChatData;
};

/**
 * RET-2349 (R4): `sendTextMessage` result.
 * - `blockedByPlan`: the plan guard refused the send (one toast, nothing sent),
 *   so the caller keeps the text, draft and reply.
 * - `sent`: the send resolved. False on every other path (plan block,
 *   read-only guard, caught error), so the caller does not report a send.
 */
export type tTextSendResult = {
  blockedByPlan: boolean;
  sent: boolean;
};

export type tChatAttachment = {
  type: string;
  src: string;
  name: string;
  size: number;
  poster: string;
  file?: File;
  duration?: number;
  // The lead this attachment was dropped/pasted onto, captured before the upload
  // (and, for HEIC, a multi-second transcode) begins. Uploading is slow enough
  // that the agent may switch leads before it finishes, and an attachment that
  // lands in the wrong tray gets delivered to the wrong person on the next
  // ordinary send. Optional so legacy/undated entries keep the old behavior.
  leadId?: string;
};

// Attachment shape expected by quick-replies-service (snake_case JSON tags).
export type tQuickReplyApiAttachment = {
  id?: string;
  mime_type: string | null;
  name: string | null;
  url: string | null;
  media_type: string;
};

export type tNewQuickAction = {
  short_command_handle: string;
  message: string;
  auth_id: string;
  workspace_id: string;
  is_public: boolean;
  attachment: tQuickReplyApiAttachment[];
  // Optional interactive payload. Shape matches quick-replies-service
  // `Reply.InteractiveConfig` (snake_case JSON tags) so the field passes
  // through the gateway unmodified. Backend cross-field validator forbids
  // coexistence with `message` / `attachment` — callers MUST send those as
  // empty when `interactive_config` is set.
  interactive_config?: tQuickReplyInteractiveConfig;
};

// Mirrors quick-replies-service `InteractiveConfig` (see PR #45). Flat
// discriminated union keyed by `type`, with variant-specific payload under
// `button` / `cta_url` / `list` (NOT under a single `action` like the
// composer uses) — so saving a draft requires translation via
// `toQuickReplyInteractiveConfig` in InteractiveAction.functions.ts.
export type tQuickReplyInteractiveConfig = {
  type: tInteractiveType;
  header?: tInteractiveHeader;
  body: string;
  footer?: string;
  button?: tInteractiveButtonAction;
  cta_url?: tInteractiveCTAUrlAction;
  list?: tInteractiveListAction;
};

export type tUpdateQuickAction = {
  id: string;
  short_command_handle: string;
  message: string;
  auth_id: string;
  workspace_id: string;
  is_public: boolean;
  attachment: tQuickReplyApiAttachment[];
};

export type tUpdateQuickActionPayload = {
  data: tUpdateQuickAction;
};

export type tQuickActionPayload = {
  data: tNewQuickAction;
};

export type tQuickActionResponse = {
  result: {
    message: string;
  };
};

export type tGetChatRequest = {
  workspaceId: string;
  leadId: string;
  integration: eChannelType;
  cursor?: string;
};

export type tGetChatIDResponse = {
  code: number;
  data: tCurrentChatID;
  errors: string[];
  message: string;
  success: boolean;
};

export type tGetChatResponse = {
  code: number;
  data: tGetChatDataResponse;
  errors: string[];
  message: string;
  success: boolean;
};

export type tGetChatLeadResponse = {
  code: number;
  data: tLead;
};

export type tAttachmentDecryption = {
  encryptedKey: string;
  iv: string;
};

export type tAttachment = {
  vcard: string | null;
  decryption: tAttachmentDecryption | null;
  duration: number | null;
  filename: string | null;
  latitude: number | null;
  longitude: number | null;
  mediaType: MediaType;
  mimeType: string | null;
  url: string | null;
  // INT-929 / SHARE_POST: TikTok shared video posts ride additional fields.
  // Each is the empty string on permanent extraction failure (per U2 backend
  // contract). No top-level `url` for SHARE_POST — by design — which makes
  // these the only renderable inputs for the SharePostContent component.
  video_id?: string;
  title?: string;
  author_name?: string;
  author_url?: string;
  thumbnail_url?: string;
  embed_url?: string;
  // INT-1703: a shared place on a LOCATION ("Farmacia San Pablo", its
  // street address); absent on a bare pin. CONTACT also carries `name`, but
  // only LOCATION renders these.
  name?: string;
  address?: string;
};

export type tGetChatDataResponse = {
  id: string;
  chatEvents: tChatEvents;
};

/** INT-1602: why a person acted on a chat's undelivered failure. */
export type tHumanActionReason = 'mensaje' | 'bloqueo';

/** INT-1602: where the person acted from: the chat list or the funnel board. */
export type tMarkSurface = 'chats' | 'embudos';

/** INT-1602: the body of POST /workspace/:wid/chat/:cid/human-action. */
export type tRecordHumanActionBody = {
  reason: tHumanActionReason;
  surface?: tMarkSurface;
};

export type tRecordHumanActionParams = tRecordHumanActionBody & {
  workspaceId: string;
  chatId: string;
};

export type tCurrentChat = {
  id: string;
  participants: tParticipant[];
  chatEvents: tChatEvents;
  driveFiles: {
    files: tDriveFile[] | null;
    cursor: string;
  };
  templates: tChatEvent[] | null;
  channels: eChannelType[] | null;
  lead: tLeadInfo;
  leadId: string;
  state: ChatState;
  integration: eChannelType;
  freeEntryPointData?: tFreeEntryPointData;
  sentTemplateCounter?: tSentTemplateCounter;
  undeliveredMessage?: tUndeliveredMessage;
  aiRespondEnabled?: boolean;
  /** WAPI contact blocked on WhatsApp via the Cloud API block endpoint (INT-1294). */
  isBlocked?: boolean;
};

export type tFreeEntryPointData = {
  FreeEntryPointFinishesAt: string;
  hasFreeEntryPoint: boolean;
};

export type tCurrentChatID = {
  id: string;
  leadId: string;
  integration: eChannelType;
  freeEntryPointData: tFreeEntryPointData;
  sentTemplateCounters?: tSentTemplateCounter;
  undeliveredMessages?: tUndeliveredMessage;
  channels: eChannelType[];
  state: string;
  /** WAPI contact blocked on WhatsApp; forwarded by bff-web from WBS (INT-1293). */
  isBlocked?: boolean;
};

/** Block state of a WAPI contact as returned by bff-web (INT-1294). */
export type tBlockContactResult = {
  is_blocked: boolean;
  blocked_at: string | null;
};

export type tBlockContactResponse = {
  code: number;
  success: boolean;
  result: tBlockContactResult;
};

export type tCalendarEvent = {
  createdBy: string;
  duration: number;
  eventType: CalendarEventType;
  id: string;
  name: string;
  startDate: string;
  note?: string;
  state?: string;
};

export type tMessage = {
  error: MessageErrorType;
  fromMe: boolean;
  isFavorite: boolean;
  isForwarded: boolean;
  participant: {
    handle: string;
    name: string;
  };
  state: MessageState;
  errorCode?: number;
};

export type tChatEventNote = {
  title: string;
};

/**
 * One page of thread events, newest-first.
 *
 * `cursor`/`hasMore` describe the page's own direction and keep their original
 * meaning. The four directional fields arrived with `?around=` (SRE-3328): an
 * anchored window sits in the middle of the thread and therefore has an edge on
 * BOTH sides, which one cursor cannot describe. bff-web populates them for the
 * one-way pages too, so the shape does not change with the anchor — but they
 * stay optional here because a cached or older bff answers without them, and a
 * `cursorAfter` that is actually `undefined` must not read as "no more".
 */
/** See `tChatContext.latestPage`. */
export type tLatestChatPage = {
  chatId: string;
  events: tChatEvent[];
  cursor: string;
  epoch: number;
};

export type tChatEvents = {
  cursor: string;
  events: tChatEvent[];
  hasMore?: boolean;
  /** Older events exist past this window's oldest; feed `cursorBefore` back. */
  hasMoreBefore?: boolean;
  cursorBefore?: string;
  /** Newer events exist past this window's newest; feed `cursorAfter` back. */
  hasMoreAfter?: boolean;
  cursorAfter?: string;
};

export type tReaction = {
  emojiUrl?: string | null;
  id: number;
  chatEventId: string;
  providerId: string;
  fromMe: boolean;
  emoji: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type tSystemSubtype =
  | 'stage-advance'
  | 'handoff'
  | 'resume'
  | 'funnel-move'
  /** LEA-50: a result of the lead, drawn from its close summary (never persisted as a message). */
  | 'lead-close';

/**
 * Reference to a funnel or stage carried on a funnel-move system event.
 * Both fields are optional: the wire payload (RET-1311) may arrive partial and
 * every consumer must tolerate absent fields without crashing.
 */
export type tSystemFunnelRef = {
  uuid?: string;
  name?: string;
};

/**
 * Structured payload for a 'funnel-move' system event, carried alongside the
 * human-readable `content.text`. Sourced from the `smartleadbot.session.funnel.moved`
 * socket event; the destination funnel `uuid` powers the in-chat deep-link. Only
 * present on the client-synthesized live event — the durable persisted note is
 * prose-only, so the deep-link is a live-note affordance.
 */
export type tSystemFunnelMove = {
  reason?: string;
  fromFunnel?: tSystemFunnelRef;
  toFunnel?: tSystemFunnelRef;
  toStage?: tSystemFunnelRef;
};

export type tSystemEvent = {
  subtype: tSystemSubtype;
  funnelMove?: tSystemFunnelMove;
  leadClose?: tCloseResult;
};

export type tChatEvent = {
  agendaEvent: tCalendarEvent | null;
  chatID: string;
  content: tContent | null;
  createdAt: string;
  createdBy: string;
  deletedAt: string;
  followUp: tFollowUp | null;
  id: string;
  message: tMessage | null;
  note: tChatEventNote | null;
  system?: tSystemEvent | null;
  providerId?: string;
  reactions?: tReaction[];
  type: EventType;
  updatedAt: string;
  index?: number;
  selectedIndex?: number;
  isWapiTemplate?: boolean;
  /**
   * BUG-741: the chat event's lifecycle state as a `ChatEventState` name
   * ("SENT", "ERROR", …). The API has always returned it (messaging's
   * `ChatEventResponseSerializer` emits `state`), but it was never modelled here,
   * so a scheduled template's real outcome was invisible to the UI.
   */
  state?: string;
  /**
   * BUG-741: failures attached to this event. For a scheduled template this
   * carries the WBS campaign's failure reason, put here by
   * `ScheduledTemplateReconciler`.
   */
  errors?: tChatEventError[];
  /**
   * AGI-243: what the Lead Agent grounded this reply on — a knowledge-base
   * file or an excerpt of operator-written material. Internal to the
   * operator; the lead never sees it. Undefined/empty for every event that
   * isn't a Lead Agent reply — the "Ver fuentes" control renders nothing.
   */
  sources?: tMessageSource[];
};

/** BUG-741: a failure recorded against a chat event. */
export type tChatEventError = {
  code?: number;
  error?: {
    context?: string;
    timestamp?: string;
    /**
     * INT-1500 C3: Meta's own numeric code, when Meta returned one. The Meta
     * error notice prefers it over mapping the internal code.
     */
    metaCode?: number;
    /** INT-1500 C3: Meta's `error_data.details`, shown as "Detalle técnico". */
    details?: string;
    /** INT-1500 C3: the whatsapp-business-service wire code of a message failure (`wba-*`). */
    wireCode?: string;
    /** INT-1500 C4: the wire code a scheduled card failed with (`wba-*`). */
    failureCode?: string;
    /**
     * INT-1500 C4, only on one of our holds: the Meta code that armed it
     * (131049 back-off, 131050 opt-out). Never Meta's code for this send.
     */
    sourceMetaCode?: number;
    /** INT-1500 C4: ISO time of the Meta failure that armed the hold. */
    sourceFailedAt?: string;
    /** INT-1500 C4: ISO time the per-lead back-off lifts. */
    backoffUntil?: string;
  } | null;
};

/**
 * AGI-243: makes every property of `T` an explicit decision at any
 * construction site typed with it.
 *
 * `tChatEvent.sources` was silently dropped THREE times by hand-rolled
 * object literals that reconstruct a `tChatEvent` from a differently-shaped
 * wire payload (the websocket `chat_event.inserted` handler in
 * `MessageContainer.component.tsx`, plus two sandbox paths). A plain object
 * literal is free to omit any optional property of `tChatEvent` and the
 * compiler stays silent — omission is indistinguishable from "not
 * applicable yet".
 *
 * `tExplicit<T>` closes that gap: every already-required key keeps its exact
 * type (so `id`, `content`, etc. still can never be `undefined`), and every
 * already-optional key becomes a REQUIRED key whose value may be
 * `undefined`. A builder must write `sources: event.sources` or
 * `sources: undefined` — it can no longer leave the key out. Adding a new
 * field to `tChatEvent` therefore breaks compilation at every construction
 * site typed `tExplicit<tChatEvent>` until someone decides what that seam
 * does with it.
 *
 * `Record<never, never>` stands in for the classic "`{}` extends
 * `Pick<T, K>` detects that K is optional" trick without writing a literal
 * `{}` type (banned by `@typescript-eslint/ban-types`).
 */
type tRequiredKeysOf<T> = {
  [K in keyof T]-?: Record<never, never> extends Pick<T, K> ? never : K;
}[keyof T];
type tOptionalKeysOf<T> = {
  [K in keyof T]-?: Record<never, never> extends Pick<T, K> ? K : never;
}[keyof T];
export type tExplicit<T> = { [K in tRequiredKeysOf<T>]: T[K] } & {
  [K in tOptionalKeysOf<T>]-?: T[K] | undefined;
};

/** AGI-243: see `tExplicit<T>` above. Used by `buildChatEventFromSocketEvent`. */
export type tChatEventExplicit = tExplicit<tChatEvent>;

/** BUG-741: the three states a scheduled-template card can be in. */
export enum eScheduledTemplateOutcome {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
}

export type tFollowUp = {
  consumed: boolean;
  triggerDate: string;
  // WBS campaign backing a WAPI scheduled template (BUG-687). Deleting the card
  // uses it to cancel the campaign. Null/absent for regular follow-ups and for
  // cards created before the link existed.
  campaignId?: number | null;
  /**
   * INT-1500 U11 (INT-1525): the chat event this card became, from messaging's
   * `follow_up.message_id`: the sent message of a regular follow-up, or the
   * TEMPLATE event of a scheduled template. Null until it goes out and on cards
   * linked before the field existed. History only: the socket strips
   * `follow_up`.
   */
  messageId?: string | null;
};

export type tDriveFile = {
  createdAt: string;
  id: string;
  mimeType: string;
  mediaType?: MediaType;
  name: string;
  updatedAt: string;
  url: string;
};

export type tLinkPreview = {
  description: string;
  thumbnail: string | null;
  title: string;
  url: string;
};

/**
 * Loose ad-context block carried inside `ad_referral.ads_context_data` for Meta
 * IG CTIM / Messenger CTM messages. The known sub-keys are declared for
 * discoverability, but the index signature keeps any future Meta sub-keys (Meta
 * has added context sub-fields before — INT-933) from being dropped. Every value
 * MUST be read defensively (coerce, never assume a string / never render an
 * object as a React child) — see
 * docs/solutions/logic-errors/unsafe-cast-masks-backend-shape-drift.md.
 */
export type tAdReferralContextData = {
  ad_title?: string;
  photo_url?: string;
  video_url?: string;
  [key: string]: unknown;
};

/**
 * Meta ad-referral context (Instagram CTIM + Messenger CTM) carried on
 * `content.ad_referral` and exposed on the model as camelCase `adReferral`.
 * Mirrors the canonical ChatEventAdReferral in
 * messaging/libs/types/src/chat/event/AdReferral.ts (kept in lockstep with the
 * wire shape `tChatEventContentAdReferral` in SocketsProvider/Sockets.types.ts).
 * Deliberately DISTINCT from the WAPI/CTWA `content.referral` (different shape:
 * source_type/source_id/source_url/thumbnail_url) — keep the two separate.
 * All fields optional: Meta delivers a partial subset (some payloads carry only
 * `ad_id`). Sub-keys stay snake_case to match the wire + canonical type.
 */
export type tAdReferral = {
  source?: string;
  type?: string;
  ad_id?: string;
  ads_context_data?: tAdReferralContextData;
};

export type tButton = {
  type: string;
  subtype?: string;
  text: string;
  payload?: string;
  index?: number;
};

// ── Interactive messages (Meta Cloud API) ───────────────────────────────────
// Event-shape contract (NOT Meta wire format). Matches chats-service emit from
// whatsapp-business-service PR #622 / messaging PR #820. Meta wire translation
// happens at the service boundary (front-leadsales sends Meta wire to bff-web;
// bff-web passes through to WBS). This shape is used in composer state and for
// reading ChatEventPg.content.interactive from WebSocket/API payloads.

export const INTERACTIVE_TYPES = ['button', 'cta_url', 'list'] as const;
export type tInteractiveType = (typeof INTERACTIVE_TYPES)[number];

export const INTERACTIVE_HEADER_TYPES = [
  'text',
  'image',
  'video',
  'document',
] as const;
export type tInteractiveHeaderType = (typeof INTERACTIVE_HEADER_TYPES)[number];

export type tInteractiveMediaRef = {
  id?: string;
  link?: string;
  // `filename` is Meta-Cloud-API-specific to document headers. It's what
  // WhatsApp shows as the document title to the recipient; without it the
  // document preview on the recipient device falls back to an ugly hashed
  // S3 filename. Optional on image/video (unused by Meta) but the shape
  // is shared for simplicity.
  filename?: string;
};

export type tInteractiveHeader = {
  type: tInteractiveHeaderType;
  text?: string;
  image?: tInteractiveMediaRef;
  video?: tInteractiveMediaRef;
  document?: tInteractiveMediaRef;
};

export type tInteractiveTextHeader = {
  type: 'text';
  text: string;
};

export type tInteractiveButton = {
  id: string;
  title: string;
};

export type tInteractiveButtonAction = {
  buttons: tInteractiveButton[];
};

export type tInteractiveCTAUrlAction = {
  display_text: string;
  url: string;
};

export type tInteractiveListRow = {
  id: string;
  title: string;
  description?: string;
};

export type tInteractiveListSection = {
  title?: string;
  rows: tInteractiveListRow[];
};

export type tInteractiveListAction = {
  button_text: string;
  sections: tInteractiveListSection[];
};

// Outbound / authoring discriminated union. `list` restricts header to text-only
// per Meta spec; switch(type) narrows action automatically.
export type tInteractiveOutbound =
  | {
      type: 'button';
      header?: tInteractiveHeader;
      body: string;
      footer?: string;
      action: tInteractiveButtonAction;
    }
  | {
      type: 'cta_url';
      header?: tInteractiveHeader;
      body: string;
      footer?: string;
      action: tInteractiveCTAUrlAction;
    }
  | {
      type: 'list';
      header?: tInteractiveTextHeader;
      body: string;
      footer?: string;
      action: tInteractiveListAction;
    };

// Inbound taps — reply-only payloads from PR #820. Outer type is
// Meta-canonical (`button_reply` / `list_reply`). Reply body is nested under
// a kind discriminator.
export type tInteractiveButtonReplyBody = {
  id: string;
  title: string;
};

export type tInteractiveListReplyBody = {
  id: string;
  title: string;
  description?: string;
};

export type tInteractiveInboundButtonReply = {
  type: 'button_reply';
  reply: { kind: 'button_reply'; button_reply: tInteractiveButtonReplyBody };
  schema_version?: string;
};

export type tInteractiveInboundListReply = {
  type: 'list_reply';
  reply: { kind: 'list_reply'; list_reply: tInteractiveListReplyBody };
  schema_version?: string;
};

// INT-1138/BUG-721 — WhatsApp Flow completion. `key` is the flow's
// technical field `name` (e.g. `screen_0_input_name_0`) and `value` is
// Meta's raw wire value, which for a selection component is the option
// *id*, not its text. INT-1321 added the resolved projections: `label`
// is the question as the operator authored it and `value_label` is the
// chosen option rendered as "2. Talla M". Both are optional — a deleted
// Flow, a runtime-bound data-source, or any answer captured before
// INT-1321 carries neither — so render `label ?? key` and
// `value_label ?? value`. `response_json` is kept for parity with the
// backend contract but is not rendered. `answers` is optional: a
// display-only flow (no user inputs) legitimately completes with zero
// answers.
export type tInteractiveFlowReplyAnswer = {
  key: string;
  value: string;
  label?: string;
  value_label?: string;
};

export type tInteractiveFlowReplyBody = {
  name?: string;
  body?: string;
  response_json: string;
  answers?: tInteractiveFlowReplyAnswer[];
  // INT-1141 — the wa_flows.uuid this completion belongs to. Absent for
  // completions captured before that linkage existed.
  flow_uuid?: string;
};

// `flow_reply` is optional because an upstream in the read path may fail to
// forward the body (BUG-728: bff-web dropped it before the passthrough fix).
// The renderer guards with `?.` and falls back to flowReplyEmpty rather than
// crashing the chat panel when the body is absent.
export type tInteractiveInboundFlowReply = {
  type: 'flow_reply';
  reply: { kind: 'flow_reply'; flow_reply?: tInteractiveFlowReplyBody };
  schema_version?: string;
};

export type tInteractiveInbound =
  | tInteractiveInboundButtonReply
  | tInteractiveInboundListReply
  | tInteractiveInboundFlowReply;

// Outbound interactive payload as stored on the event row. The composer
// builds `tInteractiveOutbound` (authoring shape); the persistence layer
// may enrich with server-side metadata (schema_version today, plus
// whatever lands later — wamid, delivery_state, etc.) without forcing the
// authoring type to widen. Callers that read from `content.interactive`
// get this shape via the `tInteractiveContent` union + narrowing guards.
export type tInteractiveOutboundStored = tInteractiveOutbound & {
  schema_version?: string;
};

// Unified shape stored in ChatEventPg.content.interactive. Outbound
// messages flow in as tInteractiveOutboundStored; inbound taps flow in
// as tInteractiveInbound.
export type tInteractiveContent =
  tInteractiveOutboundStored | tInteractiveInbound;

// Per-channel outbound *text body* caps (UTF-16 code units = JS `.length`).
// WhatsApp Cloud API rejects a longer `text.body` with Meta error 100 — the
// "Ensure your parameter is under any length restriction for the type" clause
// of its error reference. That code is a generic bucket we cannot tell apart
// after the fact, so the only place the limit can be named is here, before the
// send. Instagram and TikTok were already capped; WhatsApp was not (BUG-587).
export const CHANNEL_TEXT_LIMITS = {
  WHATSAPP: 4096,
  INSTAGRAM: 1000,
  TIKTOK: 6000,
} as const;

// Meta's server-generated field caps (UTF-16 code units = JS `.length`).
export const INTERACTIVE_LIMITS = {
  BUTTON_BODY: 1024,
  CTA_URL_BODY: 1024,
  LIST_BODY: 4096,
  FOOTER: 60,
  HEADER_TEXT: 60,
  BUTTON_ID: 256,
  BUTTON_TITLE: 20,
  BUTTON_COUNT_MIN: 1,
  BUTTON_COUNT_MAX: 3,
  CTA_DISPLAY_TEXT: 20,
  CTA_URL: 2048,
  LIST_BUTTON_TEXT: 20,
  LIST_SECTION_TITLE: 24,
  LIST_ROW_ID: 200,
  LIST_ROW_TITLE: 24,
  LIST_ROW_DESCRIPTION: 72,
  LIST_SECTION_COUNT_MIN: 1,
  LIST_SECTION_COUNT_MAX: 10,
  LIST_ROWS_TOTAL_MIN: 1,
  LIST_ROWS_TOTAL_MAX: 10,
} as const;

/**
 * Structured refs persisted with a funnel-move note (RET-1311). Snake_case
 * contents match the chats-service wire shape (and the WebSocket echo). Two
 * wrapper keys exist depending on delivery path: bff-web emits `funnelMove`
 * (REST chat-open / history), while the socket `chat_event.inserted` echo
 * relays chats-service's `funnel_move` verbatim — consumers read via
 * getContentFunnelMove, never one key directly.
 */
export type tContentFunnelMove = {
  from_funnel?: tSystemFunnelRef;
  to_funnel?: tSystemFunnelRef;
  to_stage?: tSystemFunnelRef;
};

/**
 * Carousel cards echoed back onto the sender's own template bubble (INT-1302).
 * Produced by whatsapp-business-service, relayed verbatim by chats-service and
 * bff-web, and captured byte-identical on BOTH delivery paths — so unlike
 * funnelMove/funnel_move (RET-1311) the wrapper key is the same in camel and
 * snake. DO NOT add a getContentCarousel accessor or a dual key by analogy;
 * there is nothing to reconcile.
 *
 * Deliberately typed with WhatsappApi's `tCard` rather than a parallel shape:
 * the renderer is the existing shared TemplateCarouselPreview, which consumes
 * tComponent[]/tCard[]. A bespoke structure here would not typecheck against
 * the very component this reuses.
 *
 * Wire invariants held by the producer: `schema_version` is a NUMBER (bff-web
 * normalizes it through json.Number), `type`/`format` stay UPPERCASE to match
 * eComponentType/eFormatType, card button `index` is always present (even at
 * 0), and `example.header_handle[0]` is always an http(s) URL or absent —
 * never an opaque Meta upload handle. Inner keys stay snake_case for the same
 * reason tAdReferral's do: they mirror the wire shape.
 */
export type tContentCarousel = {
  schema_version: number;
  cards: tCard[];
};

/**
 * Which carousel card the lead tapped, on an inbound quick reply (INT-1560).
 *
 * The sibling of tContentCarousel above, and easy to confuse with it: that one
 * is the OUTBOUND echo of the cards we sent, this is the INBOUND choice. An
 * event is one or the other, never both.
 *
 * It exists because Meta names no card. A tap arrives as button.{payload,text}
 * plus the template's context id, so a carousel authored the normal way — one
 * button copy repeated on every card — produced replies nothing could tell
 * apart. whatsapp-business-service encodes the card coordinates into the
 * send-time payload and decodes them back on ingest.
 *
 * Wire invariants held by the producer:
 * - Both indexes are 0-based NUMBERS, serialized even at 0, and address the
 *   cards of the QUOTED template — the event `quotedEventID` points at. They
 *   are positions, not identifiers, so bounds-check them: the quoted template
 *   is resolved separately and may predate the reply.
 * - The whole object is ABSENT, never zeroed, when the tap named no card. Every
 *   carousel delivered before INT-1560 answers with the button text, and those
 *   taps keep arriving. Absence means "unknown card" and must never be read as
 *   card 0, which is the first card. `?? 0` on this value is a bug.
 *
 * Unlike tContentCarousel — and this is the one place the analogy breaks — the
 * WRAPPER key does diverge by delivery path, so it needs an accessor exactly
 * like funnelMove/funnel_move: read it through getContentCarouselReply, never
 * one key directly. Inner keys stay snake_case, mirroring the wire shape.
 */
export type tContentCarouselReply = {
  card_index: number;
  button_index: number;
};

export type tContent = {
  tiktok?: tTikTokRichContent;
  attachments?: tAttachment[] | null;
  buttons?: tButton[];
  linkPreview?: tLinkPreview | null;
  quotedEventID?: string | null;
  text?: string | null;
  isEdited?: boolean;
  interactive?: tInteractiveContent;
  adReferral?: tAdReferral | null;
  // RET-1311 — bff REST shape (camelCase wrapper)
  funnelMove?: tContentFunnelMove | null;
  // RET-1311 — WebSocket echo shape (snake wrapper, relayed verbatim)
  funnel_move?: tContentFunnelMove | null;
  // INT-1302 — same key on the REST and socket paths, see tContentCarousel.
  carousel?: tContentCarousel | null;
  // INT-1560 — bff REST shape (camelCase wrapper)
  carouselReply?: tContentCarouselReply | null;
  // INT-1560 — WebSocket echo shape (snake wrapper, relayed verbatim)
  carousel_reply?: tContentCarouselReply | null;
};

export type tQuickReply = {
  content: tContent;
  createdAt: string;
  createdBy: string;
  isPublic: boolean;
  id: string;
  index?: number;
  selectedIndex?: number;
  setSelectedIndex?: () => void;
  shortCommand: string;
  quickActionState?: tQuickActionState;
  setQuickActionState?: (state: tQuickActionState) => void;
};

export type tGetLeadHistoryRequest = {
  workspaceId: string;
  leadId: string;
};
export interface Change {
  change: string;
  changedBy: string | null;
  createdAt: string;
  newValue: string;
  field: eField;
  previousValue: string | null;
  type: eAction;
}
export interface tGetLeadHistoryData {
  changes: Change[];
}

export type tGetLeadHistoryResponse = {
  code: number;
  data: tGetLeadHistoryData;
  errors: string[];
  message: string;
  success: boolean;
};

export type tGetNextPageRequest = {
  chatId: string;
  cursor: string;
  /**
   * Which way the page walks from the cursor. Omitted or 'back' keeps the
   * original behaviour (older events). 'forward' returns newer ones, and is
   * only ever needed after an anchored load has left the user mid-history with
   * unloaded events below them (SRE-3328).
   */
  direction?: 'back' | 'forward';
};

/**
 * `GET /chat/:cid/events?around=<eventId>` — the window CENTERED on one event
 * (SRE-3328). 12 older, the anchor, 12 newer, newest-first.
 *
 * Mutually exclusive with `cursor`/`direction` at the BFF, which is why this is
 * a separate request type rather than another optional field on the one above:
 * sending both is a 400 by construction, and the type system should say so.
 *
 * It never silently falls back to the newest page. A missing anchor is a 404
 * and an anchor belonging to another chat is a 400 — both real errors.
 */
export type tGetEventsAroundRequest = {
  chatId: string;
  eventId: string;
};

export type tGetNextPageResponse = {
  code: number;
  data: tChatEvents;
  errors: string[];
  message: string;
  success: boolean;
};

/** INT-1720: a place picked from the location search. */
export type tLocationPlace = {
  name: string;
  address: string;
};

export type tOutboundAttachment = {
  media_type: MediaType;
  url?: string;
  mimetype: string;
  latitude?: number;
  longitude?: number;
  /** INT-1720: a picked place, sent to the lead on WhatsApp API only. */
  name?: string;
  address?: string;
};

export type tSendMessageRequest = {
  chatId: string;
  data: tInsertTextMessageRequest;
};

export type tInsertTextMessageRequest = {
  id: string;
  channel_type: eChannelType;
  content: {
    text?: string;
    attachments?: tOutboundAttachment[];
    interactive?: tInteractiveOutbound & { schema_version?: string };
    tiktok?: tTikTokRichContent;
  };
  is_template: boolean;
  created_by: string;
  workspace_id: string;
};

// ── Meta wire shape for /whatsapp-api/workspaces/:wid/messages/interactive ──
// bff-web PR #643 passes this straight through to WBS PR #611 (`/meta/:wid/send/interactive`).
// Mirrors whatsappApi.types.go:206-339 on bff-web.
//
// Most Meta wire shapes are structurally identical to our authoring
// shapes — they get aliased rather than duplicated, keeping the
// authoring union as the source of truth. The genuinely wire-specific
// bits (body/footer wrapping, `buttons` array of typed reply wrappers,
// `button` vs `button_text` on lists) stay explicit.

export type tWapiSendInteractiveHeader = tInteractiveHeader;
export type tWapiSendInteractiveListRow = tInteractiveListRow;
export type tWapiSendInteractiveListSection = tInteractiveListSection;
export type tWapiSendInteractiveCTAUrlParameters = tInteractiveCTAUrlAction;

export type tWapiSendInteractiveButtonWrapper = {
  type: 'reply';
  reply: { id: string; title: string };
};

// Meta wire uses `button` (the list's CTA label) — NOT `button_text` like the
// event-shape contract. The translator in Chat.interactive.functions maps one
// to the other.
export type tWapiSendInteractiveAction =
  | { buttons: tWapiSendInteractiveButtonWrapper[] }
  | { name: 'cta_url'; parameters: tWapiSendInteractiveCTAUrlParameters }
  | { button: string; sections: tWapiSendInteractiveListSection[] };

export type tWapiSendInteractiveContent = {
  type: tInteractiveType;
  header?: tWapiSendInteractiveHeader;
  body: { text: string };
  footer?: { text: string };
  action: tWapiSendInteractiveAction;
};

export type tWapiSendInteractiveRequest = {
  to: string;
  message_id?: string;
  interactive: tWapiSendInteractiveContent;
};

// bff-web's SendInteractiveResponse shape (see
// bff-web/src/whatsappApi/whatsappApi.types.go). Flat — no wrapper, no
// `success` field. ProviderID is Meta's wamid on success.
export type tWapiSendInteractiveResponse = {
  code: number;
  message: string;
  providerId?: string;
};

// Error envelope returned by bff-web / WBS on VALIDATION_ERROR (structured per
// field so the composer can render inline messages).
export type tWapiInteractiveValidationFieldError = {
  field: string;
  code: string;
  max?: number;
  actual?: number;
  sub_type?: tInteractiveType;
};

export type tWapiInteractiveErrorBody = {
  code: string;
  message?: string;
  errors?: tWapiInteractiveValidationFieldError[];
};

export type tSendMessageResponse = {
  statusCode: number;
  message: string[];
  error: string;
};

export type tUploadFileToS3Request = {
  file: File;
  workspaceId: string;
  integration?: eChannelType;
};

export type tDeleteFileToS3Request = {
  idFile: string;
  userId: string | undefined;
  workspaceId: string | undefined;
};

export type tUploadFileToS3ResponseData = {
  url: string;
};

export type tLeadInfo = {
  /**
   * The funnel these `stages`/`statuses` were read from (BUG-288).
   *
   * A `tLeadStages` carries no back-reference to its funnel, so a caller that
   * renders this list and then writes a stage has no way to name the funnel
   * that owns it — and one that guesses from the view context sends a stage
   * of funnel A paired with funnel B, which leads-service rejects with a 404.
   * Stamped by `resetFunnelInfo` with the id it resolved; empty string means
   * the list has no owner (lead moved to the inbox).
   */
  funnelId: string;
  stages: tLeadStages[] | null;
  statuses: tLeadStatuses[] | null;
  users: tLeadUsers[] | null;
};

/** Integration channel from the API; overrides stale client-side fallbacks. */
type tLeadChatMeta = { integration?: eChannelType };

export type tLead = {
  active: boolean;
  assigneeAuthId: string;
  /** bff-web `leadbotState`; the chat popup hides a held lead from non-superusers (AGI-761). */
  leadbotState?: eLeadbotState;
  company: string | null;
  currentStage: string;
  email: string | null;
  handle: string;
  id: string;
  name: string;
  pipeId: string;
  profilePicture: string | null;
  revenue: number | null;
  stages: tLeadStages[] | null;
  status: eLeadStatus;
  statuses: tLeadStatuses[] | null;
  tags: string[] | null;
  unreadMessages: number;
  users: tLeadUsers[] | null;
  read: boolean;
  value: number | null;
  chat?: tLeadChatMeta;
};

export type tLeadStages = {
  id: string;
  name: string;
  isDefault?: boolean;
};

export type tLeadStatuses = {
  id: string;
  name: eLeadStatus;
};

export type tLeadUsers = {
  auth_id: string;
  name: string;
};

export type tChatLeadsRequest = {
  workspaceId: string;
  page: number;
  query?: string;
  active?: boolean;
  authId?: string;
  leadId?: string;
  integration: eChannelType;
};

export type tGetLeadsResponse = {
  code: number;
  data: {
    leads: tGetLeadsDataResponse[];
    isLastPage: boolean;
  };
  errors: string[];
  message: string;
  success: boolean;
};

export type tGetLeadsDataResponse = {
  active: boolean;
  assigneeAuthId: string;
  chatID?: string;
  chat: {
    /** Populated by bff-web /leads/forward (BUG-504). Used to set the top-level chatID field. */
    chatId?: string;
    integration: eChannelType;
    lastMessageOn: string | null;
    lastMessageText: string | null;
    lastMessageType: string | null;
    updatedAt: string;
    read: boolean;
    unreadCount: number;
    state: ChatState;
  };
  hasCalendarEvent: boolean;
  id: string;
  name: string;
  profilePicture: string | null;
  status: string;
};

export type tGetTemplatesRequest = {
  workspaceId: string;
  chatId: string;
};

export type tGetTemplatesResponse = {
  code: number;
  data: tChatEvent[];
  errors: string[];
  message: string;
  success: boolean;
};

export type TemplateFbIg = {
  id: number;
  name: string;
  tone: string;
  favorite: boolean;
  text: string;
  category: string;
  created_at: string;
  updated_at: string;
};

export type tFetchTemplatesResponse = {
  message: string;
  data: TemplateFbIg[];
};
export type tChangeChatState = {
  statusCode: number;
  message: string[];
  error?: string;
};

export type tChangeChatStateRequest = {
  state: ChatState;
  date: string;
  channelType: eChannelType;
};

export type tGetWorkspacesInfoRequest = {
  workspaceId: string;
  funnelId: string;
};

export type tGetWorkspacesInfoResponse = {
  code: number;
  data: tFunnelInfo;
  errors: string[];
  message: string;
  success: boolean;
};

export type tStage = {
  uuid: string;
  order: number;
  name: string;
  expires_after: number;
  count_leads: number;
  sum_value: string;
};

export type tWorkspaceUser = {
  uuid: string;
  auth_id: string;
  name: string;
  autoassignable: boolean;
  role: string;
};

export type tFunnelStatus = {
  uuid: string;
  name: string;
};

export type tFunnelInfo = {
  uuid: string;
  emoji: string;
  name: string;
  description: string;
  is_default: boolean;
  is_public: boolean;
  can_view_handle: boolean;
  from_industry_template: boolean;
  stages: tLeadStages[];
  users: tLeadUsers[];
  statuses: tLeadStatuses[];
  // BUG-534 (companion to PR #2425): set by FunnelCacheInvalidationProvider's
  // merger when a `funnel_event.updated` arrives. The wire payload strips
  // `users[]` (websocket-server V1 path) and never sets it (V2 transformer),
  // so the merger marks the cached users[] as stale and `Chat.hook.resetFunnelInfo`
  // lazily refetches via `ChatService.getWorkspacesInfo` on next read.
  // Optional / absent ≡ false. Cleared after a successful targeted refetch.
  users_stale?: boolean;
  // BUG-534: monotonic counter incremented on every merger write. Consumer-side
  // lazy refetch captures the version pre-fetch and refuses to clear
  // `users_stale` if a concurrent merge bumped the counter mid-fetch — the
  // intervening merge already marked users stale again. Absent ≡ 0 (the
  // legacy-snapshot sentinel; consumers force a refresh once on first read).
  cache_version?: number;
};

export type tWapiTemplateParameter = {
  type: string;
  format?: string;
  value: string;
  name: string;
};

export type tWapiMedia = {
  type: string;
  url: string;
  // Original document filename, forwarded to WBS only for document headers and
  // surfaced to the recipient (and the sender's own chat) as the document title
  // instead of "Sin título" (BUG-475).
  filename?: string;
};

export type tWapiButtonRequest = {
  type: 'button';
  sub_type: string;
  payload: string;
  index: number;
};

export type tWapiSendTemplateRequest = {
  to: string;
  template_id?: string;
  language?: string;
  parameters?: tWapiTemplateParameter[];
  media?: tWapiMedia;
  buttons?: tWapiButtonRequest[];
  // Firebase auth_id of the user sending the template. This request goes
  // straight to whatsapp-business-service via the legacy gateway (not bff-web),
  // so the sender must be attributed client-side here; WBS persists it to
  // ChatEvent.created_by and TemplateBubble renders the seller's name (INT-922).
  created_by?: string;
};
export type tWapiSendTemplateResponse = {
  code: number;
  errors: string[];
  message: string;
  success: boolean;
  // ID of the one-contact campaign WBS creates on the schedule path (BUG-687).
  // Snake_case: this response comes straight from whatsapp-business-service via
  // the legacy gateway. Absent on immediate sends and on pre-fix WBS versions.
  campaign_id?: number;
  // Same fact under the bff-web schedule route's camelCase key — read as a
  // fallback so a future route migration cannot silently drop the link.
  campaignId?: number;
};

// Archive/Unarchive types
export type tArchiveResponse = {
  success: boolean;
  message: string;
  code: number;
  usage: {
    used: number;
    limit: number;
  };
};

export type tParticipant = {
  id: string;
  workspaceID: string;
  handle: string;
  name: string;
};

export type tGetParticipants = {
  data: tParticipant[];
};

export type tChatSwitchCacheClass = 'cold' | 'warm';

export type tChatSwitchDurationEvent = {
  readonly chatId: string;
  readonly integration: eChannelType;
  // Time from effect start (t0) to messages queued into React state (t1).
  // Captures getOrCreateChatData + getChat fetches + setMessages scheduling —
  // pre-paint and pre-Promise.all for participants/templates/leadInfo. Previously
  // misnamed `toFirstPaintMs`; the actual post-paint metric is `toReadyMs` (t2).
  readonly toDataReadyMs: number;
  // Time from effect start (t0) to post-paint (t2) via double-rAF.
  readonly toReadyMs: number;
  readonly cacheClass: tChatSwitchCacheClass;
  readonly eventsCount: number;
  readonly connection?: string;
};
